import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import WebsiteLayout from '../components/WebsiteLayout';
import Reveal from '../components/Reveal';
import SubstrateViz from '../components/SubstrateViz';
import { useLanguage } from '../../../hooks/useLanguage';
import {
  DECISION_SUPPORT_SVG,
  PLAIN_LANGUAGE_ACCESS_SVG,
  COMPLIANCE_SVG,
} from '../data/patternIllustrations';

const HERO_VIDEO = 'https://thoughtfuloasis-public.s3.us-east-1.amazonaws.com/missionos-vnog-cocreation.mp4';

type Illustration =
  | { kind: 'svg'; html: string }
  | { kind: 'substrate' }
  | { kind: 'image'; src: string; alt: { en: string; nl: string } };

type Pattern = {
  asset: 'g1' | 'g2' | 'g3' | 'g4' | 'g5' | 'g6';
  assetLabel: { en: string; nl: string };
  liveLabel?: { en: string; nl: string };
  overline: { en: string; nl: string };
  title: { en: string; nl: string };
  statement: { en: React.ReactNode; nl: React.ReactNode };
  cta?: { prefill: string };
  illustration?: Illustration;
};

const PATTERNS: Pattern[] = [
  {
    asset: 'g1',
    assetLabel: { en: 'Data-informed decisions', nl: 'Datagedreven besluiten' },
    liveLabel: { en: 'Live', nl: 'Live' },
    overline: {
      en: 'Big-data decisions, not black boxes',
      nl: 'Datagedreven besluiten, geen black box',
    },
    title: {
      en: 'When decisions must draw on large volumes of data but cannot happen inside a black box.',
      nl: 'Wanneer besluiten moeten steunen op grote hoeveelheden data, maar niet in een black box mogen plaatsvinden.',
    },
    statement: {
      en: (
        <>
          Emergency services, operational command, clinical triage — wherever the cost of a wrong
          call is measured in lives, not clicks. The named-axis structure lets operators see
          exactly which data points drove each recommendation and why, while still processing
          everything a human alone would miss.{' '}
          <em>Fits emergency services, ER triage, operational command, military decision support.</em>
        </>
      ),
      nl: (
        <>
          Hulpdiensten, operationeel commando, klinische triage — overal waar de kosten van een
          foute beslissing in levens worden gemeten, niet in klikken. De
          benoemde-assen-structuur laat operators precies zien welke datapunten elke aanbeveling
          hebben aangestuurd en waarom, terwijl alles wordt verwerkt wat een mens alleen zou
          missen.{' '}
          <em>
            Past bij hulpdiensten, SEH-triage, operationeel commando, militaire beslisondersteuning.
          </em>
        </>
      ),
    },
    illustration: { kind: 'svg', html: DECISION_SUPPORT_SVG },
  },
  {
    asset: 'g2',
    assetLabel: { en: 'Keyword mismatch', nl: 'Keyword-mismatch' },
    liveLabel: { en: 'Live', nl: 'Live' },
    overline: {
      en: 'Plain-language access and keyword mismatch',
      nl: 'Toegang in gewone taal en keyword-mismatch',
    },
    title: {
      en: 'When users ask in everyday language but answers live in specialist jargon.',
      nl: 'Wanneer gebruikers in alledaagse taal vragen, maar antwoorden in vaktaal zijn geschreven.',
    },
    statement: {
      en: (
        <>
          Traditional search fails when the query and the answer don't share words. Our technology
          maps intent onto meaning, not keywords — bridging the gap between how citizens ask and
          how policy documents, legal codes, and specialist records are written.{' '}
          <em>Fits citizen-service portals, legal access, public services, junior-staff onboarding.</em>
        </>
      ),
      nl: (
        <>
          Traditioneel zoeken faalt wanneer de zoekvraag en het antwoord geen woorden delen. Onze
          technologie legt intentie op betekenis, niet op trefwoorden — en overbrugt zo de kloof
          tussen hoe burgers vragen en hoe beleidsdocumenten, wetteksten en specialistische
          dossiers zijn geschreven.{' '}
          <em>
            Past bij burgerportalen, juridische toegang, publieke dienstverlening, inwerken van
            junior-medewerkers.
          </em>
        </>
      ),
    },
    illustration: { kind: 'svg', html: PLAIN_LANGUAGE_ACCESS_SVG },
  },
  {
    asset: 'g5',
    assetLabel: { en: 'Solution spaces', nl: 'Oplossingsruimtes' },
    overline: { en: 'Complex solution spaces', nl: 'Complexe oplossingsruimtes' },
    title: {
      en: 'High-dimensional search where traditional systems fall short.',
      nl: 'Hoogdimensionaal zoeken waar traditionele systemen tekortschieten.',
    },
    statement: {
      en: (
        <>
          Material discovery, medication research, and other domains where the search space is
          vast and combinatorial. Named-axis spaces let researchers steer exploration along
          dimensions they understand — combining machine efficiency with expert intuition to
          navigate what brute-force methods cannot.{' '}
          <em>Fits drug-candidate screening, materials science, engineering trade-offs.</em>
        </>
      ),
      nl: (
        <>
          Materiaalontdekking, medicijnonderzoek en andere domeinen waar de zoekruimte enorm en
          combinatorisch is. Benoemde-assen-ruimtes laten onderzoekers de exploratie sturen langs
          dimensies die ze begrijpen — machinale efficiëntie gecombineerd met expertintuïtie om te
          navigeren wat brute-force-methoden niet kunnen.{' '}
          <em>Past bij screening van medicijn-kandidaten, materiaalkunde, engineering-afwegingen.</em>
        </>
      ),
    },
    cta: { prefill: 'pattern-solutionspaces' },
    illustration: { kind: 'substrate' },
  },
  {
    asset: 'g4',
    assetLabel: { en: 'Agentic AI', nl: 'Agentische AI' },
    overline: { en: 'Hard agentic problems', nl: 'Moeilijke agentische problemen' },
    title: {
      en: 'Where agents need efficient and realistic world models to act well.',
      nl: 'Waar agents efficiënte en realistische wereldmodellen nodig hebben om goed te handelen.',
    },
    statement: {
      en: (
        <>
          Autonomous agents that plan and act in the real world need compact, accurate
          representations they can reason over. Named-axis spaces give agents a world model that
          is both computationally efficient and human-inspectable — so operators can verify the
          model before the agent acts.{' '}
          <em>Fits robotics, autonomous systems, simulation, planning under uncertainty.</em>
        </>
      ),
      nl: (
        <>
          Autonome agents die plannen en handelen in de echte wereld hebben compacte, accurate
          representaties nodig om over te redeneren. Benoemde-assen-ruimtes geven agents een
          wereldmodel dat zowel rekenkundig efficiënt als menselijk inspecteerbaar is — zodat
          operators het model kunnen verifiëren voordat de agent handelt.{' '}
          <em>Past bij robotica, autonome systemen, simulatie, planning onder onzekerheid.</em>
        </>
      ),
    },
    cta: { prefill: 'pattern-agentic' },
    illustration: {
      kind: 'image',
      src: '/assets/website/illustrations/agentic.png',
      alt: {
        en: 'Isometric diagram of an agent navigating a world model toward a goal, with planned and rolled-out paths.',
        nl: 'Isometrisch diagram van een agent die door een wereldmodel naar een doel navigeert, met geplande en uitgerolde paden.',
      },
    },
  },
  {
    asset: 'g3',
    assetLabel: { en: 'Compliance', nl: 'Compliance' },
    overline: {
      en: 'Compliance and regulatory navigation',
      nl: 'Compliance en regelgeving navigeren',
    },
    title: {
      en: 'Compliance and regulatory navigation.',
      nl: 'Compliance en regelgeving navigeren.',
    },
    statement: {
      en: (
        <>
          A new regulation lands. A compliance lead has sixty days to identify which SOPs and
          equipment categories are affected. The system ranks every relevant artefact along
          interpretable risk dimensions: not "similarity 0.83" but "high jurisdiction overlap,
          moderate structural-risk overlap." The lead defends the prioritisation.{' '}
          <em>Fits regulatory change-impact, certification scoping, audit preparation.</em>
        </>
      ),
      nl: (
        <>
          Er komt nieuwe regelgeving uit. Een compliance-verantwoordelijke heeft zestig dagen om
          te bepalen welke SOP's en uitrustingscategorieën geraakt worden. De AI-laag rangschikt
          elk relevant artefact langs interpreteerbare risico-dimensies: niet "similarity 0.83"
          maar "hoge overlap op jurisdictie, gematigde overlap op structureel risico". De
          verantwoordelijke verdedigt de prioritering.{' '}
          <em>Past bij impact-analyse bij regelwijziging, certificeringsscope, audit-voorbereiding.</em>
        </>
      ),
    },
    cta: { prefill: 'pattern-compliance' },
    illustration: { kind: 'svg', html: COMPLIANCE_SVG },
  },
  {
    asset: 'g6',
    assetLabel: { en: 'Knowledge scouting', nl: 'Kennis scouten' },
    overline: {
      en: 'Knowledge scouting and triage',
      nl: 'Kennis scouten en triage',
    },
    title: { en: 'Knowledge scouting and triage.', nl: 'Kennis scouten en triage.' },
    statement: {
      en: (
        <>
          A research-transfer office gets hundreds of new publications a week. Most are noise; a
          few are commercially or industrially relevant. Domain experts explain what relevance
          looks like; the system builds the ontology around that, and alerts only when a paper
          crosses a threshold that matters.{' '}
          <em>Fits patent landscaping, deal-flow scoring, news monitoring.</em>
        </>
      ),
      nl: (
        <>
          Een transfer-office krijgt honderden nieuwe publicaties per week binnen. Het meeste is
          ruis; een paar zijn commercieel of industrieel relevant. Domeinexperts leggen uit hoe
          relevantie eruitziet; de AI-laag bouwt daar de ontologie omheen, en alarmeert pas als
          een paper een drempel overschrijdt die ertoe doet.{' '}
          <em>Past bij patent-landscaping, deal-flow scoring, nieuwsmonitoring.</em>
        </>
      ),
    },
    cta: { prefill: 'pattern-knowledge' },
  },
];

const Applications: React.FC = () => {
  const [language] = useLanguage();
  const en = language === 'en';

  const customCta = (
    <div className="site-end__cta">
      <span className="site-end__label">{en ? 'Talk to us' : 'Praat met ons'}</span>
      <h2 className="site-end__title">
        {en
          ? "If your problem fits one of these shapes, or one we haven't named yet, we'd like to hear about it."
          : "Past je probleem in een van deze vormen, of in een vorm die we nog niet benoemd hebben? Dan horen we het graag."}
      </h2>
      <div className="site-end__action">
        <Link className="btn btn-primary" to="/contact?prefill=pilot">
          {en ? 'Contact us' : 'Neem contact op'}
        </Link>
      </div>
    </div>
  );

  return (
    <WebsiteLayout customCta={customCta}>
      <Helmet>
        <title>Applications · Thoughtful Oasis</title>
        <meta
          name="description"
          content="Two early pilots in Dutch public service, plus six problem patterns we recognise across regulated and operational sectors."
        />
        <link rel="canonical" href="https://thoughtful-oasis.com/applications" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Applications · Thoughtful Oasis" />
        <meta
          property="og:description"
          content="Two early pilots in Dutch public service, plus six problem patterns we recognise across regulated and operational sectors."
        />
        <meta property="og:url" content="https://thoughtful-oasis.com/applications" />
        <meta property="og:image" content="https://thoughtful-oasis.com/assets/website/photos/og-home.jpg" />
        <meta property="og:locale" content={en ? 'en_GB' : 'nl_NL'} />
      </Helmet>

      <header className="hero hero--sub">
        <div className="hero__inner">
          <span className="label">{en ? 'Applications' : 'Toepassingen'}</span>
          <h1 style={{ marginTop: 20 }}>{en ? 'Where this is running.' : 'Waar dit draait.'}</h1>
          <p className="hero__sub">
            {en
              ? 'Two pilots in Dutch public service. Six problem patterns we recognise across regulated and operational work.'
              : 'Twee pilots in de Nederlandse publieke sector. Zes probleempatronen die we terugzien in gereguleerd en operationeel werk.'}
          </p>
        </div>
      </header>

      {/* Live pilots */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'Live pilots' : 'Live pilots'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en ? 'Two pilots, in production.' : 'Twee pilots, in productie.'}
            </h2>
          </Reveal>

          <Reveal as="article" className="case-feature">
            <div className="case-feature__image case-feature__image--olive case-feature__image--has-media">
              <div className="case-feature__media">
                <video autoPlay muted loop playsInline preload="metadata" src={HERO_VIDEO} />
                <div className="case-feature__media-overlay"></div>
              </div>
              <span className="case-feature__bubble">
                <span className="case-feature__bubble-dot"></span>
                <span className="case-feature__bubble-live">Live</span>
                <span>· MissionOS</span>
              </span>
              <p className="case-feature__pull">
                {en
                  ? 'From incident to scenario analysis in under one minute.'
                  : 'Van incident naar scenario-analyse in minder dan één minuut.'}
              </p>
              <div className="case-feature__logo">
                <img
                  src="/assets/website/logos/vnog.png"
                  alt="Veiligheidsregio Noord- en Oost-Gelderland (VNOG) — logo"
                />
              </div>
            </div>
            <div className="case-feature__body">
              <p className="case-feature__label">
                {en
                  ? 'Case 01 · Public safety, Netherlands'
                  : 'Case 01 · Publieke veiligheid, Nederland'}
              </p>
              <h3
                style={{
                  fontFamily: 'var(--f-display)',
                  fontWeight: 500,
                  fontSize: '1.5rem',
                  margin: 0,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.25,
                }}
              >
                {en
                  ? 'VNOG: incident command under time pressure.'
                  : 'VNOG: incidentbestrijding onder tijdsdruk.'}
              </h3>
              <p className="case-feature__summary">
                {en
                  ? 'Veiligheidsregio Noord- en Oost-Gelderland is the regional safety authority for the north and east of Gelderland. Incident commanders arrive on-scene with seconds to decide, and the right protocol sits in a 200-page SOP no one can scroll on a moving truck.'
                  : "Veiligheidsregio Noord- en Oost-Gelderland is de regionale veiligheidsregio voor noord- en oost-Gelderland. Bevelvoerders komen ter plaatse met seconden om te beslissen, en het juiste protocol staat in een SOP van tweehonderd pagina's dat niemand op een rijdende tankautospuit doorbladert."}
              </p>
              <ul className="case-feature__usps">
                <li>
                  {en
                    ? 'Under one minute from incident to a scenario analysis on the data the truck is carrying. 150+ simulations to date.'
                    : 'Binnen één minuut van incident naar een scenario-analyse op de data die op de wagen staat. Inmiddels 150+ simulaties.'}
                </li>
                <li>
                  {en
                    ? 'Every output traceable to the named axes that drove it: equipment, era, hazard class, jurisdiction.'
                    : 'Elke output herleidbaar tot de benoemde assen die hem aandreven: uitrustingsstuk, periode, gevaarsklasse, jurisdictie.'}
                </li>
                <li>
                  {en
                    ? 'Commanders can challenge any result by checking which dimensions produced it.'
                    : 'Bevelvoerders kunnen elk resultaat ter discussie stellen door te kijken welke dimensies het produceerden.'}
                </li>
              </ul>
              <Link className="link case-feature__link" to="/mission-os">
                {en ? 'Visit MissionOS' : 'Bezoek MissionOS'}
              </Link>
            </div>
          </Reveal>

          <Reveal as="article" id="pilot-jenv" className="case-feature">
            <div className="case-feature__image case-feature__image--mesh case-feature__image--shot">
              <span className="case-feature__bubble">
                <span className="case-feature__bubble-dot"></span>
                <span className="case-feature__bubble-live">Live</span>
                <span>· VraagMijnOverheid</span>
              </span>
              <div className="case-feature__shot-card">
                <img
                  src="/assets/website/photos/vmo-screenshot.png"
                  alt="VraagMijnOverheid interface"
                />
              </div>
              <div className="case-feature__logo case-feature__logo--color">
                <img
                  src="/assets/website/logos/jenv-rijkslint.svg"
                  alt="Ministerie van Justitie en Veiligheid"
                />
              </div>
            </div>
            <div className="case-feature__body">
              <p className="case-feature__label">
                {en
                  ? 'Case 02 · Government, Netherlands'
                  : 'Case 02 · Overheid, Nederland'}
              </p>
              <h3
                style={{
                  fontFamily: 'var(--f-display)',
                  fontWeight: 500,
                  fontSize: '1.5rem',
                  margin: 0,
                  letterSpacing: '-0.01em',
                  lineHeight: 1.25,
                }}
              >
                {en
                  ? 'Ministry of Justice and Security: plain-language access to public records.'
                  : 'Ministerie van Justitie en Veiligheid: openbare documenten in gewone taal.'}
              </h3>
              <p className="case-feature__summary">
                {en
                  ? 'Citizens file freedom-of-information requests under the Wet open overheid (Woo) in everyday language. The documents that answer those requests are written in legal and administrative register. VraagMijnOverheid closes the gap.'
                  : 'Burgers dienen Woo-verzoeken (Wet open overheid) in alledaagse taal in. De documenten die die verzoeken beantwoorden zijn geschreven in juridisch-ambtelijk register. VraagMijnOverheid overbrugt dat gat.'}
              </p>
              <ul className="case-feature__usps">
                <li>
                  {en
                    ? '66,000 documents indexed in the same interpretable space as the citizen request.'
                    : '66.000 documenten geïndexeerd in dezelfde interpreteerbare ruimte als de burgervraag.'}
                </li>
                <li>
                  {en
                    ? 'Plain-language intent mapped onto published records along named axes the policy team can read and defend.'
                    : 'Vraag in gewone taal gekoppeld aan openbare documenten langs benoemde assen die het beleidsteam kan lezen en verdedigen.'}
                </li>
                <li>
                  {en
                    ? 'Pre-clerk answers where the request is already in the public record; defensible ranking where a clerk is needed.'
                    : 'Antwoorden vóór een ambtenaar erbij komt waar het al openbaar is; verdedigbare prioritering waar een ambtenaar wel nodig is.'}
                </li>
              </ul>
              <Link className="link case-feature__link" to="/vmo">
                {en ? 'Read more' : 'Lees meer'}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Patterns */}
      <section className="section">
        <div className="container">
          <Reveal className="section-head">
            <span className="label">{en ? 'Patterns' : 'Patronen'}</span>
            <h2 style={{ marginTop: 18 }}>
              {en ? 'Six shapes of problem.' : 'Zes vormen van probleem.'}
            </h2>
            <p className="lede" style={{ marginTop: 24 }}>
              {en
                ? 'Interpretable AI by design unlocks domains where black-box machine learning cannot operate — not because of scale, but because of trust, language, complexity, or autonomy.'
                : 'Interpreteerbare AI van opzet ontsluit domeinen waar black-box machine learning niet kan opereren — niet vanwege schaal, maar vanwege vertrouwen, taal, complexiteit of autonomie.'}
            </p>
          </Reveal>

          <div className="patterns-grid patterns-grid--experiment">
            {[0, 2, 4].map((rowStart) => (
              <div className="patterns-row" key={rowStart}>
                {PATTERNS.slice(rowStart, rowStart + 2).map((p, i) => (
                  <Reveal className="pattern-card" key={rowStart + i}>
                    <div className={`pattern-card__asset pattern-card__asset--${p.asset}`}>
                      {p.illustration?.kind === 'svg' && (
                        <div
                          className="pattern-card__illustration"
                          dangerouslySetInnerHTML={{ __html: p.illustration.html }}
                        />
                      )}
                      {p.illustration?.kind === 'substrate' && (
                        <div className="pattern-card__illustration pattern-card__illustration--substrate">
                          <SubstrateViz
                            id={`pattern-viz-${rowStart + i}`}
                            className="pattern-card__substrate"
                            sweepDeg={0}
                            periodSec={1}
                            background="none"
                            viewBoxRadius={240}
                          />
                        </div>
                      )}
                      {p.illustration?.kind === 'image' && (
                        <div className="pattern-card__illustration pattern-card__illustration--image">
                          <img
                            src={p.illustration.src}
                            alt={en ? p.illustration.alt.en : p.illustration.alt.nl}
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                      )}
                      {p.liveLabel && (
                        <span className="pattern-card__live">
                          <span className="pattern-card__live-dot"></span>
                          <span className="pattern-card__live-label">
                            {en ? p.liveLabel.en : p.liveLabel.nl}
                          </span>
                        </span>
                      )}
                      <span className="pattern-card__asset-label">
                        {en ? p.assetLabel.en : p.assetLabel.nl}
                      </span>
                    </div>
                    <div className="pattern-card__body">
                      <p className="pattern-card__overline">{en ? p.overline.en : p.overline.nl}</p>
                      <h3 className="pattern-card__title">{en ? p.title.en : p.title.nl}</h3>
                      <p className="pattern-card__statement">
                        {en ? p.statement.en : p.statement.nl}
                      </p>
                      {p.cta && (
                        <Link
                          className="btn btn-primary pattern-card__cta"
                          to={`/contact?prefill=${p.cta.prefill}`}
                        >
                          {en ? 'Partner with us' : 'Werk met ons samen'}
                        </Link>
                      )}
                    </div>
                  </Reveal>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>
    </WebsiteLayout>
  );
};

export default Applications;
